import { ButtonLink } from "design-system/atoms/Button"
import { Skeleton } from "design-system/atoms/Skeleton"
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "design-system/molecules/Card"
import { ArrowUpRight } from "lucide-react"
import { NPSBadge } from "modules/shared/components/Miscelanea/NPSBadge"
import { getSentimentLabel } from "modules/shared/lib/sentiment"
import { useTranslation } from "react-i18next"
import { CustomerResponseDataSchema, GetSentimentResponseDataSchema } from "schema"
import { z } from "zod"
import { CustomerSentimentChart } from "./CustomerSentimentChart"

type Props = {
  customer: z.infer<typeof CustomerResponseDataSchema>
  customerSentiment: z.infer<typeof GetSentimentResponseDataSchema>
  isPending?: boolean
  isSentimentPending?: boolean
  currentTab?: "tickets" | "questions"
}

export const CustomerSentiment = ({
  customer,
  customerSentiment,
  isPending,
  isSentimentPending,
  currentTab,
}: Props) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col sm:flex-row gap-0 w-full h-64">
      <CustomerSentimentChart
        customerSentiment={customerSentiment}
        isPending={isSentimentPending}
      />
      <div className="flex flex-row sm:flex-col h-full flex-grow">
        <Card className="flex-1 w-full sm:w-56 rounded-none rounded-bl-xl sm:rounded-b-none sm:rounded-tr-xl flex flex-col">
          <CardHeader className="px-4 py-3 flex-grow flex flex-col justify-between">
            <CardDescription>{t("customers.sentiment.overall.title")}</CardDescription>
            <CardTitle>
              {isPending ? (
                <Skeleton className="w-full h-10" />
              ) : (
                <span className="text-lg font-medium">
                  {customerSentiment.overallSentiment
                    ? getSentimentLabel(customerSentiment.overallSentiment, t)
                    : "-"}
                </span>
              )}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="flex-1 w-full sm:w-56 rounded-none border-x-0 sm:border-x-1 sm:border-t-0 sm:border-b-0 flex flex-col">
          <CardHeader className="px-4 py-3 flex-grow flex flex-col justify-between">
            <CardDescription>{t("customers.sentiment.latestNps.title")}</CardDescription>
            <CardTitle>
              {isPending ? (
                <Skeleton className="w-full h-10" />
              ) : (
                <div className="text-lg font-medium">
                  {customer.latestNps ? (
                    <NPSBadge nps={customer.latestNps} className="text-sm border-none pl-0" />
                  ) : (
                    "-"
                  )}
                </div>
              )}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="flex-1 w-full sm:w-56 rounded-none rounded-br-xl sm:rounded-b-none sm:rounded-br-xl flex flex-col">
          <CardHeader className="px-4 py-3 flex-grow flex flex-col justify-between">
            <CardAction>
              <ButtonLink
                variant="ghost"
                size="icon"
                to="/support/list"
                search={{ supportTab: currentTab || "tickets" }}
              >
                <ArrowUpRight />
              </ButtonLink>
            </CardAction>
            <CardDescription>{t("customers.sentiment.totalTickets.title")}</CardDescription>
            <CardTitle>
              {isPending ? (
                <Skeleton className="w-full h-10" />
              ) : (
                <div className="text-lg font-medium">{customer.totalTickets}</div>
              )}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    </div>
  )
}
